import { SMap, TMap } from '@client/new/helpers/types';
import { FieldErrors } from 'react-hook-form';
import { ISection } from './schemas';
import {
  disabledSectionTypePostfix,
  EmailCaptureSectionSchema,
  SectionSchema,
} from './OTPLandingPage.schema';

export const sectionTitlesMap: Partial<
  TMap<ISection['type'] | 'FeaturesNew', string>
> = {
  OTPFeatures: 'Features',
  FeaturesNew: 'Features',
  TextSection1: 'Headline + text + media',
  TextSection2: 'Headline + text + 2 images',
  ProgramStats: 'Program info',
  EmailCapture: 'Email Capture',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toErrors = <T extends SMap<any>>(
  sections: ISection[],
  e: FieldErrors<T>,
) => {
  if (!e.sections || !Array.isArray(e.sections)) return null;

  const messages: { names: string[]; msg: string }[] = [];
  e.sections.forEach((v, i) => {
    if (!v || i >= sections.length) return null;
    const title = toSectionTitle(sections[i]);
    Object.keys(v).forEach((k) => {
      const error = v[k];
      if (!error?.message) return;
      const names = [`#sections.${i}`, `sections.${i}.${k}`];
      const errorMsg = error.message;
      // if (errorMsg === 'Cannot be empty') errorMsg = `${camelCaseToTitle(k)} cannot be empty`;
      const msg = `Section ${title}: ${errorMsg}`;

      if (messages.find((m) => m.msg === msg)) return;
      messages.push({ msg, names });
    });
  });

  if (messages.length === 0) return null;
  return {
    title:
      messages.length > 1 ? 'There are several problems' : 'There is a problem',
    messages,
  };
};

export const toSectionTitle = (
  section: Pick<SectionSchema | ISection, 'type' | 'enabled'>,
) => {
  let title = `${
    sectionTitlesMap[section.type.replace(disabledSectionTypePostfix, '')] ||
    section.type.replace(disabledSectionTypePostfix, '')
  }`;
  if (
    (section.type === 'EmailCapture' ||
      section.type === 'EmailCapture-disabled') &&
    (section as EmailCaptureSectionSchema)?.displayAsPopup
  )
    title = `Popup ${title}`;

  if (section.enabled === false) title = `Disabled - ${title}`;
  return title;
};

export const onEnabledChange = <T extends Pick<SectionSchema, 'type'>>(
  section: T,
  enabled: boolean,
): Partial<SectionSchema> => {
  if (section.type === 'Introduction')
    return { enabled: true, type: section.type };

  const type = enabled
    ? section.type.replace(disabledSectionTypePostfix, '')
    : !section.type.endsWith(disabledSectionTypePostfix)
    ? `${section.type}${disabledSectionTypePostfix}`
    : section.type;

  return { enabled, type } as Partial<SectionSchema>;
};

export const extendSectionsType = (sections: ISection[]) =>
  sections.map((v) => {
    if (v.enabled === false && !v.type.endsWith(disabledSectionTypePostfix))
      return {
        ...v,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        type: `${v.type}${disabledSectionTypePostfix}` as any,
      } as ISection;
    return v;
  });

export const cleanupSectionsType = (sections: ISection[]) =>
  sections.map((value) => {
    if (value.type.endsWith(disabledSectionTypePostfix))
      return {
        ...value,
        type: value.type.replace(disabledSectionTypePostfix, ''),
        enabled: value.type.endsWith(disabledSectionTypePostfix)
          ? false
          : value.enabled,
      } as ISection;
    return value;
  });
